import { useCallback, useEffect, useRef } from 'react';
import type { BusinessFormValues } from '../components/business-form';

/**
 * Keeps onboarding answers across a refresh.
 *
 * The wizard previously held every answer in component state only, so a reload
 * at the last field returned the user to screen one with an empty form and no
 * way to recover what they had typed. This mirrors the campaign builder's
 * existing draft pattern, scoped to the signed-in account rather than to a
 * business, because during onboarding no business exists yet.
 *
 * The logo is deliberately not persisted: a `File` cannot be serialised, and
 * re-picking it is a single click.
 */

const DRAFT_KEY_PREFIX = 'pathiq_onboarding_draft';
const MAX_DRAFT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface OnboardingDraftValues {
  language: string;
  business: Omit<BusinessFormValues, 'logo'>;
}

interface StoredDraft extends OnboardingDraftValues {
  timestamp: string;
  userId: string;
}

export const getOnboardingDraftKey = (userId: string) =>
  `${DRAFT_KEY_PREFIX}_${userId}`;

interface UseOnboardingDraftOptions {
  /** Drafts are never read or written until the account is known. */
  userId?: string;
  values: { language: string; business: BusinessFormValues };
  onRestore: (draft: OnboardingDraftValues) => void;
}

export const useOnboardingDraft = ({
  userId,
  values,
  onRestore,
}: UseOnboardingDraftOptions) => {
  const hasRestored = useRef(false);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  const clearDraft = useCallback(() => {
    if (!userId) return;
    try {
      localStorage.removeItem(getOnboardingDraftKey(userId));
    } catch (error) {
      console.warn('Failed to clear onboarding draft:', error);
    }
  }, [userId]);

  // Restore once, as soon as the account id is known.
  useEffect(() => {
    if (!userId || hasRestored.current) return;
    hasRestored.current = true;

    try {
      const raw = localStorage.getItem(getOnboardingDraftKey(userId));
      if (!raw) return;

      const draft = JSON.parse(raw) as StoredDraft;
      if (draft.userId !== userId) return;

      const age = Date.now() - new Date(draft.timestamp).getTime();
      if (!Number.isFinite(age) || age > MAX_DRAFT_AGE_MS) {
        localStorage.removeItem(getOnboardingDraftKey(userId));
        return;
      }

      onRestoreRef.current({
        language: draft.language,
        business: draft.business,
      });
    } catch (error) {
      console.warn('Failed to restore onboarding draft:', error);
    }
  }, [userId]);

  // Save on every change, but only after the restore pass, so an empty initial
  // render cannot overwrite a good draft.
  useEffect(() => {
    if (!userId || !hasRestored.current) return;

    const { logo: _logo, ...business } = values.business;
    const isEmpty =
      !values.language &&
      Object.values(business).every((value) => !value);
    if (isEmpty) return;

    try {
      const draft: StoredDraft = {
        language: values.language,
        business,
        timestamp: new Date().toISOString(),
        userId,
      };
      localStorage.setItem(
        getOnboardingDraftKey(userId),
        JSON.stringify(draft)
      );
    } catch (error) {
      console.warn('Failed to save onboarding draft:', error);
    }
  }, [userId, values]);

  return { clearDraft };
};
